import { Store, ActionContext as VueActionContext } from 'vuex'
import { OrderStatusRequest, OrderStatusResponse, OrderSubmitRequest, OrderSubmitResponse } from '@/types/order.types'
import { getStatus, postOrder } from '@/services/order-service'
import { resolveToftaException } from '@/services/error-mapping.service'

export const NAMESPACE = 'orderSubmitStore'

export enum Property {
  OrderSubmitResponse = 'orderSubmitResponse',
  OrderStatusResponse = 'orderStatusResponse',
  Loading = 'loading',
  Exception = 'exception',
  ExceptionTag = 'exceptionTag',
  ExceptionMessage = 'exceptionMessage',
  SubmitOrder = 'submitOrder',
  FetchOrderStatus = 'fetchOrderStatus',
  CleanSubmitException = 'cleanSubmitException'
}

type State = {
  [Property.OrderSubmitResponse]: OrderSubmitResponse | null;
  [Property.OrderStatusResponse]: OrderStatusResponse | null;
  [Property.Loading]: boolean;
  [Property.Exception]: boolean;
  [Property.ExceptionTag]: string | null;
  [Property.ExceptionMessage]: string | null;
}

const namespaced = (getter: Property): string => `${NAMESPACE}/${getter}`

type OrderSubmitStoreType = Store<State>

export const getLoading = (store: OrderSubmitStoreType): boolean => store.getters[namespaced(Property.Loading)]
export const getException = (store: OrderSubmitStoreType): boolean => store.getters[namespaced(Property.Exception)]
export const getExceptionMessage = (store: OrderSubmitStoreType): boolean => store.getters[namespaced(Property.ExceptionMessage)]
export const getExceptionTag = (store: OrderSubmitStoreType): string => store.getters[namespaced(Property.ExceptionTag)]
export const getOrderSubmitResponse = (store: OrderSubmitStoreType): OrderSubmitResponse => store.getters[namespaced(Property.OrderSubmitResponse)]
export const getOrderStatusResponse = (store: OrderSubmitStoreType): OrderStatusResponse => store.getters[namespaced(Property.OrderStatusResponse)]

export const submitOrderRequest = (store: OrderSubmitStoreType, request: OrderSubmitRequest): Promise<void> => store.dispatch(namespaced(Property.SubmitOrder), request)
export const fetchOrderStatus = (store: OrderSubmitStoreType, request: OrderStatusRequest): Promise<void> => store.dispatch(namespaced(Property.FetchOrderStatus), request)
export const cleanSubmitException = (store: OrderSubmitStoreType): Promise<void> => store.dispatch(namespaced(Property.CleanSubmitException))

export type ActionContext = VueActionContext<State, {}>

const OrderSubmitStore = {
  namespaced: true,
  state: {
    [Property.OrderSubmitResponse]: null,
    [Property.OrderStatusResponse]: null,
    [Property.Loading]: false,
    [Property.Exception]: false,
    [Property.ExceptionTag]: null,
    [Property.ExceptionMessage]: null
  },
  getters: {
    [Property.OrderSubmitResponse]: (state: State) => state[Property.OrderSubmitResponse],
    [Property.OrderStatusResponse]: (state: State) => state[Property.OrderStatusResponse],
    [Property.Loading]: (state: State) => state[Property.Loading],
    [Property.Exception]: (state: State) => state[Property.Exception],
    [Property.ExceptionTag]: (state: State) => state[Property.ExceptionTag],
    [Property.ExceptionMessage]: (state: State) => state[Property.ExceptionMessage]
  },
  mutations: {
    [Property.OrderSubmitResponse]: (state: State, response: OrderSubmitResponse | null) => state[Property.OrderSubmitResponse] = response,
    [Property.OrderStatusResponse]: (state: State, response: OrderStatusResponse | null) => state[Property.OrderStatusResponse] = response,
    [Property.Loading]: (state: State, loading: boolean) => state[Property.Loading] = loading,
    [Property.Exception]: (state: State, exception: boolean) => state[Property.Exception] = exception,
    [Property.ExceptionTag]: (state: State, exceptionTag: string) => state[Property.ExceptionTag] = exceptionTag,
    [Property.ExceptionMessage]: (state: State, exceptionMessage: string) => state[Property.ExceptionMessage] = exceptionMessage
  },
  actions: {
    [Property.SubmitOrder]: async ({ commit }: VueActionContext<State, {}>, request: OrderSubmitRequest) => {
      try {
        commit(Property.Loading, true)
        commit(Property.Exception, false)

        const response = await postOrder(request)

        commit(Property.OrderSubmitResponse, response)
      } catch (err) {
        commit(Property.ExceptionMessage, resolveToftaException(err))
        commit(Property.ExceptionTag, err.response.data?.Tag)
        commit(Property.Exception, true)
      } finally {
        commit(Property.Loading, false)
      }
    },
    [Property.FetchOrderStatus]: async ({ commit }: VueActionContext<State, {}>, request: OrderStatusRequest) => {
      try {
        commit(Property.Loading, true)
        commit(Property.Exception, false)

        const response = await getStatus(request)

        commit(Property.OrderStatusResponse, response)
      } catch (err) {
        commit(Property.ExceptionMessage, resolveToftaException(err))
        commit(Property.Exception, true)
      } finally {
        commit(Property.Loading, false)
      }
    },
    [Property.CleanSubmitException]: ({ commit }: VueActionContext<State, {}>) => {
      commit(Property.Exception, false)
      commit(Property.ExceptionTag, null)
    }
  }
}

export default OrderSubmitStore
